/* eslint-disable react/no-multi-comp */
import i18n from '../../../i18n/translate';
import { getData } from '../../../json-api/JsonApiUtils';
import { sortBy as lodashSortBy } from 'lodash';
import getPageSize from '../../../util/pageSize';
import { ScrollElementToTarget } from '../../../util/WindowHelper';
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import FormControl from '../Controls/FormControl';
import Button from '../../Button';
import Icon from '../../Icon';
import Chip from '../../Chip';
import { pickDataAttributes } from '../../../Util';


export const autoComplete = (props) => {
  return props.autocomplete && props.autocomplete === 'off' ?
    { autoComplete: 'nope', autoCorrect: 'off', spellCheck: 'off' } : {};
}

export const checkError = (props, select) => {
  if (typeof props.setError === 'function') {
    const input = ReactDOM.findDOMNode(select).querySelector('input');
    if (props.required && (!input || !input.value)) {
      props.setError(true, i18n.t("This field is required"));
    } else {
      props.setError(false);
    }
  }
}

export const getSelectData = async (input, props) => {
  const { authToken, buildDisplay, dataUrl, disabledOptionsIds,
    displayProperty, pageSize, queryFields, onDataReady, sortBy,
    optionsToAppend, optionsToPrepend, valueProperty } = props;

  let queryParams;
  if (queryFields && input) {
    queryParams = {
      name: {
        term: input,
        fields: queryFields
      }
    };
  }

  const { data } = await getData(dataUrl, {
    authToken,
    filters: {
      ...props.filters, ...(
        props.filterField && input && !queryFields ? { [props.filterField]: input } : undefined
      )
    },
    sorts: sortBy && [{ value: sortBy, direction: 'asc' }],
    page: { number: 1, size: pageSize || getPageSize() },
    query: queryParams
  });

  let options = data.map((i) => {
    const buildDisplayVal = buildDisplay && buildDisplay(i);
    const disabled = !!disabledOptionsIds.length && disabledOptionsIds.includes(i.id);
    return {
      [valueProperty]: i[valueProperty],
      data: i,
      disabled,
      display: buildDisplayVal || `${i[displayProperty]}`
    }
  });

  options = lodashSortBy(options, option => option.display.toLowerCase()); // Temporary sorting until we get sorted results from api

  /*
    postGetOptions. Similar to buildDisplay except
    used for taking ALL options, making changes, then sending
    them all back in as one object.
  */
  if(props.postGetOptions) {
    options = await props.postGetOptions(options);
  }

  if (optionsToPrepend) options = optionsToPrepend.concat(options);
  if (optionsToAppend) options = optionsToAppend.concat(options);

  if (onDataReady) onDataReady(options);
  return options
}

export const selectOnChange = (value, props, select, skipValueChange) => {
  if (typeof props.onChange === 'function') {
    props.onChange(value);
  }
  if (typeof props.onValueChange === 'function' && !skipValueChange) {
    let changedValue = null;
    if (value && value[props.valueProperty || props.valueKey]) {
      changedValue = value[props.valueProperty || props.valueKey];
    } else if (value instanceof Array) {
      changedValue = value.map((val) => val[props.valueProperty || props.valueKey]);
    }
    props.onValueChange({ [props.name]: changedValue });
  }
  if (!value && select && props.dataUrl) {
    select.loadOptions(null);
  }

  return value;
}

export const onFocus = (e, props) => {
  let el = !props.scrollContainerId ? window : document.getElementById(props.scrollContainerId)
  let target = e.target;
  ScrollElementToTarget(el, target, 200);
  if (typeof props.onFocus === 'function') {
    props.onFocus(e);
  }
}

export const translationProps = {
  searchPromptText: i18n.t("Type to search"),
  noResultsText: i18n.t("No results found"),
  loadingPlaceholder: i18n.t("Loading..."),
  clearAllText: i18n.t("Clear all"),
  clearValueText: i18n.t("Clear value")
};

export const defaultMultiselectProps = Object.assign({}, FormControl.defaultProps, {
  creatable: false,
  disabledOptionsIds: [],
  filters: {},
  placeholderText: '',
  requireInput: false,
  valueChangeDelay: 500,
  valueProperty: 'id'
});

export const rendererProps = (multi, props = null) => {
  const dataAttributeProps = props && pickDataAttributes(props);

  const SingleValueComponent = val => {
    const { children, id } = val;
    return (
      <div
        className="Select-value"
        {...dataAttributeProps}
      >
        <span
          aria-selected="true"
          className="Select-value-label"
          id={id}
          role="option"
        >
          {children}
        </span>
      </div>
    );
  }

  const MultiValueComponent = val => {
    const { children, disabled, onClick, onRemove, value } = val;
    const performOnClick = event => {
      event.preventDefault();
      event.stopPropagation();
      if (onClick) {
        onClick(value);
      }
    }
    const performOnRemove = event => {
      event.preventDefault();
      event.stopPropagation();
      onRemove(value);
    }

    return (
      <Chip
        value={React.Children.toArray(children)[0]}
        disabled={disabled}
        onClick={performOnClick}
        onRemove={performOnRemove}
      />
    );
  };

  return {
    arrowRenderer: () => <Icon iconType="chevron-down" />,
    clearRenderer: () => <Button style="link" icon="close" />,
    valueComponent: multi ? MultiValueComponent : SingleValueComponent
  }
}

export const multiSelectPropTypes = {
  authToken: PropTypes.string,
  buildDisplay: PropTypes.func,
  cacheEnabled: PropTypes.bool,
  children: PropTypes.any,
  creatable: PropTypes.bool,
  dataUrl: PropTypes.string.isRequired,
  dataValue: PropTypes.any,
  disabled: PropTypes.bool,
  disabledOptionsIds: PropTypes.array,
  displayProperty: PropTypes.string,
  draggable: PropTypes.bool,
  filterField: PropTypes.string.isRequired,
  filters: PropTypes.object,
  initialData: PropTypes.any,
  onChange: PropTypes.func,
  onDataReady: PropTypes.func,
  optionsToAppend: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    display: PropTypes.string
  })),
  optionsToPrepend: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    display: PropTypes.string
  })),
  pageSize: PropTypes.number,
  postGetOptions: PropTypes.func,
  queryFields: PropTypes.object,
  requireInput: PropTypes.bool,
  scrollContainerId: PropTypes.string,
  sortBy: PropTypes.string,
  valueChangeDelay: PropTypes.number,
  valueProperty: PropTypes.string
}
